import React, { useMemo } from 'react';
import { createSearchParams, generatePath, useNavigate, useParams } from 'react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { SalvButton, SalvLabel, SalvProgressCircle, SalvTable, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import SalvPage from '../../../common/SalvPage';
import { InternalNavigation } from '../../../navigation/helper';
import { formatDate } from '../../../common/datetime/FormattedDateTime';
import { screeningTestResultDashboardLink } from '../../../dashboard/dashboardHelpers';
import { InternalApiScreeningFlowTestRun } from '../../../api-internal';
import { SearchParamKeys } from '../../../common/searchParamKeys';
import { getScreeningFlowDisplayId } from '../../configure/new/helper';
import { useQueryScreeningFlowDraftTestRuns } from './useQueryScreeningFlowDraftTestRuns';
import { formatEnumLabel } from '../../../common/helper';

const STATUS_VARIANT: Record<string, 'primary' | 'secondary' | 'danger' | 'warning' | 'success'> = {
  FINISHED: 'success',
  RUNNING: 'primary',
  FAILED: 'danger',
  SCHEDULED: 'warning',
  NEW: 'secondary',
  CANCELLED: 'secondary',
};

const formatDuration = (ms?: number | null): string => {
  if (ms == null) {
    return '-';
  }
  if (ms < 1000) {
    return `${ms} ms`;
  }
  return `${(ms / 1000).toFixed(1)} s`;
};

const PREVIEW_ENTITY_IDS = 3;

const renderRecords = (run: InternalApiScreeningFlowTestRun): React.ReactNode => {
  if (run.selectionMode === 'ENTITY_IDS') {
    const ids = run.externalEntityIds ?? [];
    if (ids.length === 0) {
      return 'Specific IDs';
    }
    if (ids.length <= PREVIEW_ENTITY_IDS) {
      return ids.join(', ');
    }
    return (
      <SalvTooltipWrapper tooltip={ids.join(', ')} placement='top'>
        <span>{`${ids.slice(0, PREVIEW_ENTITY_IDS).join(', ')} +${ids.length - PREVIEW_ENTITY_IDS} more`}</span>
      </SalvTooltipWrapper>
    );
  }
  if (run.fromTime && run.toTime) {
    return `${formatDate(run.fromTime as Date)} – ${formatDate(run.toTime as Date)}`;
  }
  return '-';
};

const formatScreenedOnly = (run: InternalApiScreeningFlowTestRun): string => {
  if (run.selectionMode !== 'DATE_RANGE') {
    return '—';
  }
  return run.useOnlyScreenedEntities ? 'Yes' : 'No';
};

const ScreeningFlowDraftTestRuns = () => {
  const { flowId, draftId } = useParams<{ flowId: string; draftId: string }>();
  const navigate = useNavigate();
  const { data: testRuns, isPending } = useQueryScreeningFlowDraftTestRuns(Number(draftId));

  const entityType = testRuns?.[0]?.entityType;
  const breadcrumbs = [
    {
      title: 'Screening flows',
      to: {
        pathname: InternalNavigation.screeningFlows,
        search: entityType ? createSearchParams({ [SearchParamKeys.entityType]: entityType }).toString() : undefined,
      },
    },
    {
      title: getScreeningFlowDisplayId(flowId!),
      to: { pathname: generatePath(InternalNavigation.screeningFlow, { flowId: flowId! }) },
    },
  ];

  const columnHelper = createColumnHelper<InternalApiScreeningFlowTestRun>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('screeningTestId', { header: 'ID' }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ getValue }) => (
          <SalvLabel variant={STATUS_VARIANT[getValue()] ?? 'secondary'}>{formatEnumLabel(getValue())}</SalvLabel>
        ),
      }),
      columnHelper.accessor('createdTime', {
        header: 'Created',
        cell: ({ getValue }) => formatDate(getValue() as Date, true),
      }),
      columnHelper.display({
        header: 'Duration',
        cell: ({ row: { original } }) => formatDuration(original.durationMs),
      }),
      columnHelper.display({
        header: 'Records',
        cell: ({ row: { original } }) => renderRecords(original),
      }),
      columnHelper.display({
        header: 'Goodlisting',
        cell: ({ row: { original } }) => (original.goodlistEnabled ? 'Yes' : 'No'),
      }),
      columnHelper.display({
        header: 'Screened only',
        cell: ({ row: { original } }) => formatScreenedOnly(original),
      }),
      columnHelper.display({
        header: 'Actions',
        cell: ({ row: { original } }) => (
          <SalvButton
            label='View results'
            variant='secondary'
            size={Size.SMALL}
            disabled={original.status !== 'FINISHED'}
            onClick={() => navigate(screeningTestResultDashboardLink(original.screeningTestId))}
          />
        ),
      }),
    ],
    [navigate]
  );

  return (
    <SalvPage title='Test runs' breadcrumb={breadcrumbs}>
      {isPending ? (
        <SalvProgressCircle ariaLabel='Loading test runs' />
      ) : (
        <SalvTable columns={columns} data={testRuns ?? []} emptyMessage='No tests have been run against this draft' />
      )}
    </SalvPage>
  );
};

export default ScreeningFlowDraftTestRuns;
